import '@babylonjs/core/XR/features/WebXRAnchorSystem';
import '@babylonjs/core/XR/features/WebXRHitTest';
import '@babylonjs/core/XR/features/WebXRPlaneDetector';
import { Scene } from '@babylonjs/core/scene';
import { KbViewer } from '@kb3d/view/kb-viewer';
import { Subject } from 'rxjs';
import { first } from 'rxjs/operators';
import { GLTFKb3dExporter } from './exporter-gltf';
import { USDZBabylonConverter } from './usdz/usdz-babylon-converter';
import { Xr } from './xr/xr';

const IS_IOS =
    /iPad|iPhone|iPod/.test(navigator.platform) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); //https://stackoverflow.com/a/58065241

const IS_ANDROID = /android/i.test(navigator.userAgent);

export class AR {
    currentObjectUrl?: string;
    isDebug = false;

    constructor(private v: KbViewer, private scene: Scene) {
        this.isDebug = !!v.options.debugMode;
        this.gltfExporter = new GLTFKb3dExporter(scene);
    }

    public xrActive = false;
    public xrLoading = false;
    public onSessionEnd = new Subject<void>();

    private _xr: Xr;
    private gltfExporter: GLTFKb3dExporter;

    public toggle() {
        if (this.xrActive) {
            return this.end();
        } else {
            return this.start();
        }
    }

    public start(overlayElem?: HTMLElement, onEnterFunc?: () => PromiseLike<unknown>) {
        // Have to use a callback because android should run this after entering XR, but ios must run it before entering XR
        if (!onEnterFunc) {
            onEnterFunc = () => Promise.resolve();
        }
        const _onEnterFunc = onEnterFunc;
        if (IS_ANDROID) {
            return this.startWebXR(overlayElem).then(() => _onEnterFunc());
        } else if (IS_IOS || this.isDebug) {
            const usdz = new USDZBabylonConverter();
            if (this.v.sceneNode) {
                const self = this;

                return this.v
                    .whenAssetsLoaded()
                    .then(() => _onEnterFunc())
                    .then(() => this.v.forceAndWaitForNextRender())
                    .then(() => usdz.parse(this.scene, this.v.sceneNode, true))
                    .then(res => {
                        if (!res) {
                            return;
                        }
                        const a = document.createElement('a');

                        if (a.relList.supports('ar') || this.isDebug) {
                            if (self.currentObjectUrl) {
                                window.URL.revokeObjectURL(self.currentObjectUrl);
                                self.currentObjectUrl = undefined;
                            }

                            self.currentObjectUrl = window.URL.createObjectURL(res);

                            const img = document.createElement('img');
                            img.src = '/images/ar_ios.png';
                            a.appendChild(img);
                            document.body.appendChild(a);
                            a.href = self.currentObjectUrl;
                            a.download = 'test.usdz';
                            a.rel = 'ar';

                            // Fire XR Exit right away on iOS since the USDZ is already generated and static now
                            // So, whatever scene changes they want when the user exits AR should already be in the browser
                            this.xrActive = false;
                            this.xrLoading = false;
                            this.onSessionEnd.next();

                            setTimeout(() => {
                                a.click();

                                setTimeout(() => {
                                    document.body.removeChild(a);
                                }, 0);
                            }, 50);

                            return undefined;
                        }

                        return Promise.reject('Your device does not support AR.');
                    });
            } else {
                return Promise.reject('Scene not initialized');
            }
        }

        return Promise.reject('Your device does not support AR.');
    }

    public end() {
        if (IS_ANDROID) {
            return this.endWebXr();
        } else if (IS_IOS) {
            if (this.currentObjectUrl) {
                URL.revokeObjectURL(this.currentObjectUrl);
            }
            //TODO usdz
        }

        return Promise.resolve();
    }

    public startWebXR(overlayElem?: HTMLElement) {
        this._xr = new Xr(this.v, this.scene);
        this._xr.onSessionEnd.pipe(first()).subscribe(() => {
            this.xrActive = false;
            this.xrLoading = false;
            this.onSessionEnd.next();
        });
        this.xrLoading = true;
        return this._xr.startSession('immersive-ar', 'local-floor', overlayElem).then(() => {
            this.xrActive = true;
            this.xrLoading = false;
        });
    }

    protected endWebXr() {
        if (this._xr) {
            return this._xr.endSession();
        } else {
            return Promise.resolve();
        }
    }
    /**
     * Takes a URL to a USDZ file and sets the appropriate fields so that Safari
     * iOS can intent to their AR Quick Look.
     */
    public startIosQuickLook(usdzSrc: string, canScale: boolean) {
        const anchor = document.createElement('a');
        anchor.setAttribute('rel', 'ar');
        anchor.appendChild(document.createElement('img'));

        const modelUrl = new URL(usdzSrc, self.location.toString());
        if (!canScale) {
            modelUrl.hash = 'allowsContentScaling=0';
        }
        anchor.setAttribute('href', modelUrl.toString());
        anchor.click();
    }

    /**
     * Android SceneViewer is a native app.  You build an intent link to tell
     * the browser to open scene viewer and show a GLB.  It does not accept blob urls,
     * so the link must go to a file on the server:  https://github.com/google/model-viewer/issues/617     *
     */
    // public startAndroidSceneViewer(glbSrc: string) {
    //     this.gltfExporter.generateGlb().then(blob => {
    //         let objectUrl = window.URL.createObjectURL(blob);
    //             window.URL.revokeObjectURL(objectUrl);
    //         //TODO:  need to send GLB to server, as sceneViewer won't accept blob urls from the browser
    //         const a = document.createElement('a');
    //         const fallbackUrl = encodeURIComponent(self.location.toString());
    //         /**temp hack */ objectUrl =
    //             'https://storage.googleapis.com/ar-answers-in-search-models/static/Tiger/model.glb';
    //         const href = `intent://arvr.google.com/scene-viewer/1.0?file=${objectUrl}&mode=ar_preferred#Intent;scheme=https;package=com.google.android.googlequicksearchbox;action=android.intent.action.VIEW;S.browser_fallback_url=${fallbackUrl};end;`;
    //         a.setAttribute('href', href);
    //         a.click();
    //     });
    // }
}
